import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useBusiness } from "@/hooks/use-business";
import { useToast } from "@/hooks/use-toast";
import { useNotifications } from "@/hooks/use-notifications";
import { supabase } from "@/integrations/supabase/client";
import { Calendar as CalendarIcon, Copy, ExternalLink, Loader2 } from "lucide-react";
import { format, startOfWeek, parseISO } from "date-fns";
import { getBaseUrl } from "@/lib/site";

const DOW: Array<{ n: number; label: string }> = [
  { n: 1, label: "Mon" },
  { n: 2, label: "Tue" },
  { n: 3, label: "Wed" },
  { n: 4, label: "Thu" },
  { n: 5, label: "Fri" },
  { n: 6, label: "Sat" },
  { n: 7, label: "Sun" },
];

function slugify(s: string) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-+|-+$)/g, "")
    .slice(0, 48);
}

function copyToClipboard(text: string): Promise<boolean> {
  const s = String(text || "").trim();
  if (!s) return Promise.resolve(false);
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(s).then(() => true).catch(() => false);
  }
  try {
    const el = document.createElement("textarea");
    el.value = s;
    el.setAttribute("readonly", "true");
    el.style.position = "fixed";
    el.style.top = "0";
    el.style.left = "0";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.focus();
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
    return Promise.resolve(true);
  } catch {
    return Promise.resolve(false);
  }
}

type OrdersTab = "calendar" | "submissions" | "feedback" | "buffet";

type OrdersProps = {
  initialTab?: OrdersTab;
  hideTabs?: boolean;
  headerTitle?: string;
  headerDescription?: string;
};

export default function Orders({
  initialTab = "calendar",
  hideTabs = false,
  headerTitle,
  headerDescription,
}: OrdersProps) {
  const { business, loading: businessLoading, refetch } = useBusiness();
  const { toast } = useToast();
  const { counts, markTypeRead } = useNotifications();
  const sb = supabase as any;

  const primary = ((business as any)?.primary_module || "appointments") as string;
  const isCateringServices = primary === "catering_services" || primary === "food_vendors";
  const canUse = isCateringServices;
  const eventCateringEnabled = isCateringServices && (business as any)?.event_catering_requests_enabled === true;

  const [activeTab, setActiveTab] = useState<OrdersTab>(() => initialTab);
  const submissionsUnread = Number(counts["food_order_submission_created"] || 0);
  const buffetUnread = Number(counts["catering_request_created"] || 0);
  const cateringRequestUrl = eventCateringEnabled && (business as any)?.slug
    ? `${getBaseUrl()}/catering/${(business as any).slug}/request`
    : "";

  useEffect(() => {
    if (activeTab === "submissions" && submissionsUnread > 0) {
      markTypeRead("food_order_submission_created");
    }
    if (activeTab === "buffet" && buffetUnread > 0) {
      markTypeRead("catering_request_created");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "buffet" && !eventCateringEnabled) {
      setActiveTab("calendar");
    }
  }, [activeTab, eventCateringEnabled]);

  const [loading, setLoading] = useState(true);
  const [menus, setMenus] = useState<any[]>([]);
  const [selectedMenuId, setSelectedMenuId] = useState<string | null>(null);
  const selectedMenu = useMemo(() => menus.find((m) => m.id === selectedMenuId) || null, [menus, selectedMenuId]);
  const menusByWeekStart = useMemo(() => {
    const map = new Map<string, any>();
    for (const m of menus) map.set(String(m.week_start_date), m);
    return map;
  }, [menus]);
  const selectedWeekDate = useMemo(() => {
    if (!selectedMenu?.week_start_date) return undefined;
    try {
      return parseISO(String(selectedMenu.week_start_date));
    } catch {
      return undefined;
    }
  }, [selectedMenu?.week_start_date]);

  const [items, setItems] = useState<any[]>([]);
  const itemsByDay = useMemo(() => {
    const map: Record<number, any[]> = {};
    for (const it of items) {
      const d = Number(it.day_of_week);
      if (!map[d]) map[d] = [];
      map[d].push(it);
    }
    for (const k of Object.keys(map)) {
      map[Number(k)] = map[Number(k)].sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
    }
    return map;
  }, [items]);

  const [links, setLinks] = useState<any[]>([]);
  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(null);
  const selectedLink = useMemo(() => links.find((l) => l.id === selectedLinkId) || null, [links, selectedLinkId]);

  // Submissions are aggregated per company/link
  const [summaries, setSummaries] = useState<any[]>([]);
  const [detailsByLink, setDetailsByLink] = useState<Record<string, any[]>>({});
  const [includeWeekends, setIncludeWeekends] = useState(false);
  const [detailsView, setDetailsView] = useState<"meal" | "employee">("meal");

  // Feedback submitted from the public orders page
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackRows, setFeedbackRows] = useState<any[]>([]);
  const [feedbackSearch, setFeedbackSearch] = useState("");

  // Buffet / event catering requests
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requests, setRequests] = useState<any[]>([]);
  const [editRequestOpen, setEditRequestOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState<any | null>(null);
  const [editReq, setEditReq] = useState<any>({
    company_name: "",
    event_date: "",
    attendees: "0",
    budget_amount: "",
    service_style: "buffet",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    location: "",
    notes: "",
    status: "new",
  });

  const fetchRequests = async () => {
    if (!business?.id) return;
    setRequestsLoading(true);
    try {
      const { data, error } = await sb
        .from("catering_requests")
        .select("*")
        .eq("business_id", business.id)
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      setRequests(data || []);
    } catch (e: any) {
      toast({ title: "Failed to load requests", description: e?.message || "Try again.", variant: "destructive" });
      setRequests([]);
    } finally {
      setRequestsLoading(false);
    }
  };

  const updateRequestStatus = async (id: string, status: string) => {
    try {
      const { error } = await sb
        .from("catering_requests")
        .update({ status })
        .eq("id", id);
      if (error) throw error;
      await fetchRequests();
    } catch (e: any) {
      toast({ title: "Failed to update status", description: e?.message || "Try again.", variant: "destructive" });
    }
  };

  const createInvoiceFromRequest = async (r: any) => {
    if (!business?.id) return;
    try {
      const currency = (business as any)?.currency || "GHS";
      const billToName = String(r.company_name || r.contact_name || "Client");
      const issueDate = new Date().toISOString().slice(0, 10);
      const budget = r.budget_amount != null ? Number(r.budget_amount) : null;
      const attendees = Number(r.attendees || 0);
      const eventDate = r.event_date ? String(r.event_date) : "";
      const style = String(r.service_style || "buffet");

      const notesParts = [
        budget != null ? `Budget: ${currency} ${budget}` : "",
        attendees ? `Attendees: ${attendees}` : "",
        eventDate ? `Event date: ${eventDate}` : "",
        style ? `Service style: ${style}` : "",
        r.location ? `Location: ${r.location}` : "",
      ].filter(Boolean);

      const invPayload: any = {
        business_id: business.id,
        customer_id: null,
        status: "draft",
        currency,
        issue_date: issueDate,
        due_date: null,
        payment_terms: null,
        bill_to_name: billToName,
        bill_to_email: r.contact_email || null,
        bill_to_phone: r.contact_phone || null,
        notes: (notesParts.join("\n") || null) as any,
        terms: null,
      };

      const { data: created, error: cErr } = await sb.from("invoices").insert(invPayload).select("*").single();
      if (cErr) throw cErr;

      // Make this invoice "editable by default": per-guest line item with qty = attendees.
      const qty = Math.max(1, attendees || 0);
      const unitPrice = budget != null && qty > 0 ? budget / qty : 0;
      const desc = `Catering (${style}) • per guest${eventDate ? ` • ${eventDate}` : ""}`;
      const { error: iErr } = await sb.from("invoice_items").insert({
        business_id: business.id,
        invoice_id: created.id,
        position: 1,
        description: desc,
        quantity: qty,
        unit_price: unitPrice,
      });
      if (iErr) throw iErr;

      const { error: rErr } = await sb
        .from("catering_requests")
        .update({ invoice_id: created.id, status: "invoiced" })
        .eq("id", r.id);
      if (rErr) throw rErr;

      toast({ title: "Draft invoice created" });
      await fetchRequests();
      // Open the invoice editor directly
      window.open(`${getBaseUrl()}/app/invoicing?edit=${created.id}`, "_blank");
    } catch (e: any) {
      toast({ title: "Failed to create invoice", description: e?.message || "Try again.", variant: "destructive" });
    }
  };

  const openEditRequest = (r: any) => {
    setEditingRequest(r);
    setEditReq({
      company_name: r.company_name || "",
      event_date: r.event_date || "",
      attendees: String(r.attendees ?? 0),
      budget_amount: r.budget_amount != null ? String(r.budget_amount) : "",
      service_style: r.service_style || "buffet",
      contact_name: r.contact_name || "",
      contact_phone: r.contact_phone || "",
      contact_email: r.contact_email || "",
      location: r.location || "",
      notes: r.notes || "",
      status: r.status || "new",
    });
    setEditRequestOpen(true);
  };

  const saveEditedRequest = async () => {
    if (!editingRequest?.id) return;
    try {
      const payload: any = {
        company_name: editReq.company_name?.trim() || null,
        event_date: editReq.event_date?.trim() || null,
        attendees: Math.max(0, Number(editReq.attendees || 0)),
        budget_amount: editReq.budget_amount?.trim() ? Number(editReq.budget_amount) : null,
        service_style: String(editReq.service_style || "buffet"),
        contact_name: String(editReq.contact_name || "").trim(),
        contact_phone: editReq.contact_phone?.trim() || null,
        contact_email: editReq.contact_email?.trim() || null,
        location: editReq.location?.trim() || null,
        notes: editReq.notes?.trim() || null,
        status: String(editReq.status || "new"),
      };
      if (!payload.contact_name) {
        toast({ title: "Contact name is required", variant: "destructive" });
        return;
      }
      const { error } = await sb.from("catering_requests").update(payload).eq("id", editingRequest.id);
      if (error) throw error;
      toast({ title: "Request updated" });
      setEditRequestOpen(false);
      setEditingRequest(null);
      await fetchRequests();
    } catch (e: any) {
      toast({ title: "Failed to save request", description: e?.message || "Try again.", variant: "destructive" });
    }
  };

  const fetchFeedback = async (menuId: string) => {
    if (!business?.id || !menuId) return;
    setFeedbackLoading(true);
    try {
      const { data, error } = await sb
        .from("food_order_feedback")
        .select(
          `
          id,
          created_at,
          employee_name,
          message,
          link_id,
          link:food_order_links(
            id,
            company_name,
            slug
          )
        `,
        )
        .eq("business_id", business.id)
        .eq("menu_id", menuId)
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      setFeedbackRows(data || []);
    } catch (e: any) {
      toast({ title: "Failed to load feedback", description: e?.message || "Try again.", variant: "destructive" });
      setFeedbackRows([]);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const fetchAll = async (menuId?: string) => {
    if (!business?.id) return;
    setLoading(true);
    try {
      const { data: m, error: mErr } = await sb
        .from("food_weekly_menus")
        .select("*")
        .eq("business_id", business.id)
        .order("week_start_date", { ascending: false })
        .limit(52);
      if (mErr) throw mErr;
      setMenus(m || []);
      const nextMenuId = menuId || selectedMenuId || (m && m[0]?.id) || null;
      setSelectedMenuId(nextMenuId);
      setSelectedLinkId(null);
      setDetailsByLink({});

      if (nextMenuId) {
        const { data: it, error: itErr } = await sb
          .from("food_menu_items")
          .select("*")
          .eq("menu_id", nextMenuId)
          .order("day_of_week", { ascending: true })
          .order("position", { ascending: true });
        if (itErr) throw itErr;
        setItems(it || []);

        const { data: lnk, error: lErr } = await sb
          .from("food_order_links")
          .select("*")
          .eq("menu_id", nextMenuId)
          .order("created_at", { ascending: false });
        if (lErr) throw lErr;
        setLinks(lnk || []);

        // Load company summaries for Submissions tab
        const { data: sum, error: sErr } = await sb.rpc("get_food_order_link_summaries", {
          _business_id: business.id,
          _menu_id: nextMenuId,
        });
        if (sErr) throw sErr;
        setSummaries(sum || []);

        // Load feedback for this week/menu
        await fetchFeedback(nextMenuId);
      } else {
        setItems([]);
        setLinks([]);
        setSummaries([]);
        setFeedbackRows([]);
      }
    } catch (e: any) {
      toast({ title: "Failed to load orders", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!business?.id) return;
    fetchAll();
    if (business?.id) fetchRequests();
  }, [business?.id]);

  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [menuWeekStart, setMenuWeekStart] = useState<string>("");
  const [menuTitle, setMenuTitle] = useState<string>("");
  const [cutoffTime, setCutoffTime] = useState<string>("10:00");
  const [deliveryStart, setDeliveryStart] = useState<string>("12:00");
  const [deliveryEnd, setDeliveryEnd] = useState<string>("14:00");
  const [creatingMenu, setCreatingMenu] = useState(false);

  const createMenu = async () => {
    if (!business?.id) return;
    if (!menuWeekStart) return;
    setCreatingMenu(true);
    try {
      const payload = {
        business_id: business.id,
        week_start_date: menuWeekStart,
        title: menuTitle.trim() || null,
        cutoff_time: cutoffTime || null,
        delivery_start_time: deliveryStart || null,
        delivery_end_time: deliveryEnd || null,
        currency: (business as any)?.currency || "GHS",
      };
      const { data, error } = await sb.from("food_weekly_menus").insert(payload).select("*").single();
      if (error) throw error;
      setNewMenuOpen(false);
      setMenuTitle("");
      toast({ title: "Weekly menu created" });
      await fetchAll(data?.id);
    } catch (e: any) {
      toast({ title: "Failed to create menu", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setCreatingMenu(false);
    }
  };

  const [addMealOpen, setAddMealOpen] = useState(false);
  const [mealDay, setMealDay] = useState<number>(1);
  const [mealRows, setMealRows] = useState<Array<{ name: string; price: string; description: string }>>([
    { name: "", price: "0", description: "" },
  ]);
  const [addingMeal, setAddingMeal] = useState(false);

  const addMealsBatch = async () => {
    if (!business?.id || !selectedMenuId) return;
    setAddingMeal(true);
    try {
      const dayItems = itemsByDay[mealDay] || [];
      const basePos = dayItems.length;

      const clean = mealRows
        .map((r) => ({
          name: (r.name || "").trim(),
          description: (r.description || "").trim(),
          price: r.price,
        }))
        .filter((r) => !!r.name);

      if (clean.length === 0) {
        toast({ title: "Add at least one meal name", variant: "destructive" });
        return;
      }

      const payload = clean.map((r, idx) => ({
        business_id: business.id,
        menu_id: selectedMenuId,
        day_of_week: mealDay,
        name: r.name,
        description: r.description || null,
        price_amount: Number(r.price || 0),
        position: basePos + idx + 1,
      }));

      const { error } = await sb.from("food_menu_items").insert(payload);
      if (error) throw error;
      setAddMealOpen(false);
      setMealRows([{ name: "", price: "0", description: "" }]);
      toast({ title: "Meals added" });
      await fetchAll(selectedMenuId);
    } catch (e: any) {
      toast({ title: "Failed to add meals", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setAddingMeal(false);
    }
  };

  // Companies are customers for food vendors (select from customers table)
  const [companyPickerOpen, setCompanyPickerOpen] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [companyResults, setCompanyResults] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null);
  const [creatingLink, setCreatingLink] = useState(false);

  const searchCompanies = async (q: string) => {
    if (!business?.id) return;
    const query = q.trim();
    const { data } = await sb
      .from("customers")
      .select("id,name,email,phone")
      .eq("business_id", business.id)
      .ilike("name", `%${query || ""}%`)
      .order("created_at", { ascending: false })
      .limit(20);
    setCompanyResults(data || []);
  };

  useEffect(() => {
    if (!companyPickerOpen) return;
    const t = setTimeout(() => searchCompanies(companySearch), 200);
    return () => clearTimeout(t);
  }, [companyPickerOpen, companySearch]);

  const createOrReuseLinkForCompany = async () => {
    if (!business?.id || !selectedMenuId || !selectedCompany?.id) return;
    setCreatingLink(true);
    try {
      // Reuse if it exists
      const { data: existing } = await sb
        .from("food_order_links")
        .select("id")
        .eq("menu_id", selectedMenuId)
        .eq("customer_id", selectedCompany.id)
        .maybeSingle();
      if (existing?.id) {
        toast({ title: "Link already exists" });
        await fetchAll(selectedMenuId);
        return;
      }

      const base = slugify(selectedCompany.name) || "company";
      const suffix = Math.random().toString(16).slice(2, 6);
      const slug = `${base}-${suffix}`;
      const { error } = await sb.from("food_order_links").insert({
        business_id: business.id,
        menu_id: selectedMenuId,
        customer_id: selectedCompany.id,
        company_name: selectedCompany.name,
        slug,
      });
      if (error) throw error;
      toast({ title: "Company link created" });
      await fetchAll(selectedMenuId);
    } catch (e: any) {
      toast({ title: "Failed to create link", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setCreatingLink(false);
    }
  };

  const publicLinkUrl = useMemo(() => {
    if (!business?.slug || !selectedLink?.slug) return "";
    return `${getBaseUrl()}/orders/${business.slug}/${selectedLink.slug}`;
  }, [business, selectedLink]);

  const visibleDays = useMemo(() => {
    const hasWeekendMeals = (itemsByDay[6]?.length || 0) > 0 || (itemsByDay[7]?.length || 0) > 0;
    return DOW.filter((d) => d.n <= 5 || includeWeekends || hasWeekendMeals);
  }, [itemsByDay, includeWeekends]);

  const loadDetails = async (linkId: string) => {
    if (!business?.id) return;
    if (detailsByLink[linkId]) return;
    const { data, error } = await sb.rpc("get_food_order_link_details", {
      _business_id: business.id,
      _link_id: linkId,
    });
    if (error) throw error;
    setDetailsByLink((prev) => ({ ...prev, [linkId]: data || [] }));
  };

  const exportCompanyCSV = (linkId: string, companyName: string) => {
    const details = detailsByLink[linkId] || [];
    if (!details.length) return;
    const rows = details.map((r: any) => ({
      order_date: r.order_date,
      employee_name: r.employee_name,
      meal: r.meal_name,
      quantity: r.quantity,
      notes: r.notes || "",
    }));
    const headers = Object.keys(rows[0]);
    const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers.join(","), ...rows.map((rr: any) => headers.map((h) => esc((rr as any)[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `submissions_${slugify(companyName) || "company"}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredFeedback = useMemo(() => {
    const q = feedbackSearch.trim().toLowerCase();
    const rows = feedbackRows || [];
    if (!q) return rows;
    return rows.filter((r: any) => {
      const company = String(r?.link?.company_name || "").toLowerCase();
      const employee = String(r?.employee_name || "").toLowerCase();
      const message = String(r?.message || "").toLowerCase();
      return company.includes(q) || employee.includes(q) || message.includes(q);
    });
  }, [feedbackRows, feedbackSearch]);

  const feedbackCompaniesCount = useMemo(() => {
    const set = new Set<string>();
    for (const r of feedbackRows || []) {
      const name = String(r?.link?.company_name || "").trim();
      if (name) set.add(name);
    }
    return set.size;
  }, [feedbackRows]);

  const exportFeedbackCSV = (rows: any[]) => {
    if (!rows?.length) return;
    const mapped = rows.map((r: any) => ({
      submitted_at: r.created_at ? new Date(r.created_at).toISOString() : "",
      company: String(r?.link?.company_name || ""),
      employee_name: String(r?.employee_name || ""),
      message: String(r?.message || ""),
    }));
    const headers = Object.keys(mapped[0]);
    const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers.join(","), ...mapped.map((rr: any) => headers.map((h) => esc((rr as any)[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `feedback_${slugify(String(selectedMenu?.title || "")) || "week"}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const groupedDetailsByDateMeal = useMemo(() => {
    if (!selectedLinkId) return [];
    const rows = detailsByLink[selectedLinkId] || [];
    // group: date -> meal(normalized) -> { mealLabel, totalQty, people[] }
    const byDate = new Map<string, Map<string, { mealLabel: string; totalQty: number; people: Array<{ employee: string; qty: number; notes?: string }> }>>();
    for (const r of rows) {
      const date = String(r.order_date);
      const employee = String(r.employee_name || "").trim() || "—";
      const mealRaw = String(r.meal_name || "").trim() || "—";
      const mealKey = mealRaw.toLowerCase();
      const qty = Number(r.quantity || 0);
      const notes = String(r.notes || "").trim();

      if (!byDate.has(date)) byDate.set(date, new Map());
      const byMeal = byDate.get(date)!;
      if (!byMeal.has(mealKey)) byMeal.set(mealKey, { mealLabel: mealRaw, totalQty: 0, people: [] });
      const g = byMeal.get(mealKey)!;
      g.totalQty += qty;
      g.people.push({ employee, qty, notes: notes || undefined });
    }

    // Sort dates asc (week view feel), meals by qty desc
    const dates = Array.from(byDate.keys()).sort();
    return dates.map((date) => {
      const meals = Array.from(byDate.get(date)!.values()).sort((a, b) => b.totalQty - a.totalQty);
      return { date, meals };
    });
  }, [detailsByLink, selectedLinkId]);

  if (businessLoading) {
    return (
      <AppShell businessName="Loading...">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppShell>
    );
  }

  if (!business) {
    return (
      <AppShell businessName="My Business">
        <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">No business found.</div>
      </AppShell>
    );
  }

  if (!isCateringServices) {
    return (
      <AppShell businessName={business.name || "My Business"}>
        <PageHeader
          title={headerTitle ?? "Orders"}
          description={headerDescription ?? "Employee meal orders"}
        />
        <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
          Orders is currently available only for the Catering Services module.
        </div>
      </AppShell>
    );
  }

  // Catering Services businesses always have Orders available (module-level feature)

  return (
    <AppShell businessName={business.name || "My Business"}>
      <PageHeader
        title={headerTitle ?? "Orders"}
        description={headerDescription ?? "Weekly menus + employee submissions"}
      />

      {!hideTabs ? (
        <Tabs value={activeTab} onValueChange={(v: any) => setActiveTab(v)} className="w-full">
          <ScrollArea className="w-full whitespace-nowrap">
            <TabsList className="mb-4 sm:mb-6 bg-muted/50 p-1 rounded-lg inline-flex w-auto">
              <TabsTrigger value="calendar" className="rounded-md text-xs sm:text-sm px-3">
                Weekly menu
              </TabsTrigger>
              <TabsTrigger value="submissions" className="rounded-md text-xs sm:text-sm px-3">
                <span className="inline-flex items-center gap-2">
                  <span>Submissions</span>
                  {submissionsUnread > 0 ? (
                    <span className="inline-flex min-w-[18px] h-[18px] px-1 items-center justify-center rounded-full bg-primary text-primary-foreground text-[11px] font-semibold">
                      {submissionsUnread > 99 ? "99+" : submissionsUnread}
                    </span>
                  ) : null}
                </span>
              </TabsTrigger>
              <TabsTrigger value="feedback" className="rounded-md text-xs sm:text-sm px-3">
                Feedback
              </TabsTrigger>
              {eventCateringEnabled ? (
                <TabsTrigger value="buffet" className="rounded-md text-xs sm:text-sm px-3">
                  <span className="inline-flex items-center gap-2">
                    <span>Buffet / Events</span>
                    {buffetUnread > 0 ? (
                      <span className="inline-flex min-w-[18px] h-[18px] px-1 items-center justify-center rounded-full bg-primary text-primary-foreground text-[11px] font-semibold">
                        {buffetUnread > 99 ? "99+" : buffetUnread}
                      </span>
                    ) : null}
                  </span>
                </TabsTrigger>
              ) : null}
            </TabsList>
            <ScrollBar orientation="horizontal" className="invisible" />
          </ScrollArea>
        </Tabs>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        {activeTab !== "buffet" ? (
        <Card className="lg:col-span-1">
          <CardContent className="p-3 sm:p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold">Weekly menu</div>
              <Button size="sm" onClick={() => setNewMenuOpen(true)}>
                New
              </Button>
            </div>

            <div className="rounded-xl border border-border overflow-hidden">
              <Calendar
                mode="single"
                selected={selectedWeekDate}
                onSelect={(date) => {
                  if (!date) return;
                  const monday = startOfWeek(date, { weekStartsOn: 1 });
                  const weekStart = format(monday, "yyyy-MM-dd");
                  const existing = menusByWeekStart.get(weekStart);
                  if (existing?.id) {
                    setSelectedMenuId(existing.id);
                    fetchAll(existing.id);
                    return;
                  }
                  setMenuWeekStart(weekStart);
                  setNewMenuOpen(true);
                }}
              />
            </div>
            <div className="text-xs text-muted-foreground">
              Tap a date to open that week. If no menu exists, we’ll prompt you to create it.
            </div>

            <div className="pt-2 border-t border-border space-y-2">
              <div className="text-sm font-semibold">Share link (per company)</div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  className="w-full sm:w-auto justify-start"
                  variant="outline"
                  onClick={() => setCompanyPickerOpen(true)}
                  disabled={!selectedMenuId}
                >
                  {selectedCompany?.name ? `Company: ${selectedCompany.name}` : "Select company"}
                </Button>
                <Button
                  className="w-full sm:w-auto"
                  onClick={createOrReuseLinkForCompany}
                  disabled={!selectedMenuId || !selectedCompany?.id || creatingLink}
                >
                  {creatingLink ? "Saving..." : "Generate link"}
                </Button>
              </div>
              <div className="text-xs text-muted-foreground">
                Companies are your existing customers. Add them in Customers/Memberships, then generate a link here.
              </div>

              <Label className="text-xs text-muted-foreground">Existing links</Label>
              <Select
                value={selectedLinkId || ""}
                onValueChange={(v) => setSelectedLinkId(v)}
                disabled={!links.length}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={links.length ? "Select a link" : "No links yet"} />
                </SelectTrigger>
                <SelectContent>
                  {links.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {publicLinkUrl ? (
                <div className="space-y-2">
                  <Input value={publicLinkUrl} readOnly className="bg-muted/30 font-mono text-xs" />
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={async () => {
                      await navigator.clipboard.writeText(publicLinkUrl);
                      toast({ title: "Link copied" });
                    }}
                  >
                    Copy link
                  </Button>
                </div>
              ) : null}
            </div>
          </CardContent>
        </Card>
        ) : null}

        <Card className={activeTab === "buffet" ? "lg:col-span-3" : "lg:col-span-2"}>
          <CardContent className="p-3 sm:p-4">
            {!selectedMenu ? (
              <div className="text-sm text-muted-foreground">Create a weekly menu to get started.</div>
            ) : (
              <>
                {activeTab === "calendar" ? (
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={includeWeekends}
                        onChange={(e) => setIncludeWeekends(e.target.checked)}
                      />
                      Include weekends
                    </label>
                    <Button size="sm" className="w-full sm:w-auto" onClick={() => setAddMealOpen(true)} disabled={!selectedMenuId}>
                      Add meal
                    </Button>
                  </div>
                  {loading ? (
                    <div className="flex items-center justify-center py-10">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {visibleDays.map((d) => (
                        <div key={d.n} className="rounded-xl border border-border p-4">
                          <div className="font-semibold">{d.label}</div>
                          <div className="mt-2 space-y-2">
                            {(itemsByDay[d.n] || []).length === 0 ? (
                              <div className="text-sm text-muted-foreground">No meals yet.</div>
                            ) : (
                              (itemsByDay[d.n] || []).map((it: any) => (
                                <div key={it.id} className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium truncate">{it.name}</div>
                                    {it.description ? (
                                      <div className="text-xs text-muted-foreground truncate">{it.description}</div>
                                    ) : null}
                                  </div>
                                  <div className="text-xs text-muted-foreground whitespace-nowrap">
                                    {(selectedMenu as any)?.currency || "GHS"} {Number(it.price_amount || 0).toFixed(2)}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                ) : null}

                {activeTab === "submissions" ? (
                  <>
                    {/* Mobile cards */}
                    <div className="sm:hidden space-y-3">
                    {summaries.length === 0 ? (
                      <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
                        No submissions yet.
                      </div>
                    ) : (
                      summaries.map((s: any) => (
                        <div key={s.link_id} className="rounded-xl border border-border bg-card p-4 space-y-3">
                          <div className="font-semibold">{s.company_name}</div>
                          <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                            <div>
                              <div className="font-medium text-foreground">{Number(s.submissions_count || 0)}</div>
                              <div>Submissions</div>
                            </div>
                            <div>
                              <div className="font-medium text-foreground">{Number(s.total_qty || 0)}</div>
                              <div>Items</div>
                            </div>
                            <div>
                              <div className="font-medium text-foreground">
                                {s.last_submitted_at ? new Date(s.last_submitted_at).toLocaleDateString() : "—"}
                              </div>
                              <div>Last</div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button
                              variant="outline"
                              className="w-full"
                              onClick={async () => {
                                await loadDetails(String(s.link_id));
                                exportCompanyCSV(String(s.link_id), String(s.company_name || "company"));
                              }}
                              disabled={Number(s.submissions_count || 0) === 0}
                            >
                              Export
                            </Button>
                            <Button
                              className="w-full"
                              onClick={async () => {
                                await loadDetails(String(s.link_id));
                                setSelectedLinkId(String(s.link_id));
                              }}
                              disabled={Number(s.submissions_count || 0) === 0}
                            >
                              View
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Desktop table */}
                  <div className="hidden sm:block rounded-xl border border-border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Company</TableHead>
                          <TableHead className="text-right">Submissions</TableHead>
                          <TableHead className="text-right">Items</TableHead>
                          <TableHead className="text-right">Last</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {summaries.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-sm text-muted-foreground">
                              No submissions yet.
                            </TableCell>
                          </TableRow>
                        ) : (
                          summaries.map((s: any) => (
                            <TableRow key={s.link_id}>
                              <TableCell className="font-medium">{s.company_name}</TableCell>
                              <TableCell className="text-right">{Number(s.submissions_count || 0)}</TableCell>
                              <TableCell className="text-right">{Number(s.total_qty || 0)}</TableCell>
                              <TableCell className="text-right text-sm text-muted-foreground">
                                {s.last_submitted_at ? new Date(s.last_submitted_at).toLocaleString() : "—"}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={async () => {
                                      await loadDetails(String(s.link_id));
                                      exportCompanyCSV(String(s.link_id), String(s.company_name || "company"));
                                    }}
                                    disabled={Number(s.submissions_count || 0) === 0}
                                  >
                                    Export
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={async () => {
                                      await loadDetails(String(s.link_id));
                                      setSelectedLinkId(String(s.link_id));
                                    }}
                                    disabled={Number(s.submissions_count || 0) === 0}
                                  >
                                    View
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {selectedLinkId && detailsByLink[selectedLinkId] ? (
                    <div className="mt-4 rounded-xl border border-border p-4">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="font-semibold">Company details</div>
                        <Button variant="outline" size="sm" onClick={() => setSelectedLinkId(null)}>
                          Close
                        </Button>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                        <div className="text-xs text-muted-foreground">
                          View mode:
                        </div>
                        <div className="flex gap-2 w-full sm:w-auto">
                          <Button
                            size="sm"
                            className="flex-1 sm:flex-none"
                            variant={detailsView === "meal" ? "default" : "outline"}
                            onClick={() => setDetailsView("meal")}
                          >
                            Group by meal
                          </Button>
                          <Button
                            size="sm"
                            className="flex-1 sm:flex-none"
                            variant={detailsView === "employee" ? "default" : "outline"}
                            onClick={() => setDetailsView("employee")}
                          >
                            Raw list
                          </Button>
                        </div>
                      </div>

                      {detailsView === "meal" ? (
                        <Accordion type="multiple" className="rounded-lg border border-border overflow-hidden">
                          {groupedDetailsByDateMeal.length === 0 ? (
                            <div className="p-4 text-sm text-muted-foreground">No orders yet.</div>
                          ) : (
                            groupedDetailsByDateMeal.map((day) => (
                              <AccordionItem key={day.date} value={day.date} className="px-3 sm:px-4">
                                <AccordionTrigger className="no-underline hover:no-underline py-3 text-sm">
                                  <div className="flex items-center justify-between w-full pr-2">
                                    <div className="min-w-0 font-medium truncate">
                                      {(() => {
                                        try {
                                          const d = parseISO(day.date);
                                          return (
                                            <>
                                              <span className="sm:hidden">{format(d, "EEE")} • {day.date}</span>
                                              <span className="hidden sm:inline">{format(d, "EEEE")} • {day.date}</span>
                                            </>
                                          );
                                        } catch {
                                          return day.date;
                                        }
                                      })()}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground whitespace-nowrap">
                                      {day.meals.reduce((s, m) => s + m.totalQty, 0)} items
                                    </div>
                                  </div>
                                </AccordionTrigger>
                                <AccordionContent className="pt-2 pb-4">
                                  <div className="space-y-3">
                                    {day.meals.map((m, idx) => (
                                      <div key={`${day.date}-${idx}`} className="rounded-lg border border-border p-3">
                                        <div className="flex items-center justify-between gap-2">
                                          <div className="font-semibold text-foreground">{m.mealLabel}</div>
                                          <div className="text-sm font-semibold">{m.totalQty}</div>
                                        </div>
                                        <div className="mt-2 space-y-2">
                                          {m.people.map((p, pi) => (
                                            <div key={pi} className="flex items-start justify-between gap-3 text-sm">
                                              <div className="min-w-0">
                                                <div className="truncate">
                                                  <span className="text-muted-foreground">- </span>
                                                  <span className="font-medium">{p.employee}</span>
                                                </div>
                                                {p.notes ? (
                                                  <div className="text-xs text-muted-foreground truncate ml-4">{p.notes}</div>
                                                ) : null}
                                              </div>
                                              <div className="text-sm font-semibold">{p.qty}</div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            ))
                          )}
                        </Accordion>
                      ) : (
                        <div className="rounded-lg border border-border overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Employee</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Meal</TableHead>
                                <TableHead className="text-right">Qty</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {detailsByLink[selectedLinkId].map((r: any, idx: number) => (
                                <TableRow key={`${r.submission_id}-${idx}`}>
                                  <TableCell className="font-medium">{r.employee_name}</TableCell>
                                  <TableCell className="text-sm">{String(r.order_date)}</TableCell>
                                  <TableCell className="max-w-[420px] truncate">{r.meal_name}</TableCell>
                                  <TableCell className="text-right">{Number(r.quantity || 0)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  ) : null}
                  </>
                ) : null}

                {activeTab === "feedback" ? (
                  <div className="space-y-3">
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className="rounded-xl border border-border bg-card p-4">
                        <div className="text-xs text-muted-foreground">Feedback messages</div>
                        <div className="text-2xl font-semibold">{Number(feedbackRows?.length || 0)}</div>
                      </div>
                      <div className="rounded-xl border border-border bg-card p-4">
                        <div className="text-xs text-muted-foreground">Companies</div>
                        <div className="text-2xl font-semibold">{feedbackCompaniesCount}</div>
                      </div>
                      <div className="rounded-xl border border-border bg-card p-4">
                        <div className="text-xs text-muted-foreground">Last received</div>
                        <div className="text-sm font-semibold">
                          {feedbackRows?.[0]?.created_at ? new Date(feedbackRows[0].created_at).toLocaleString() : "—"}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        placeholder="Search company, employee, or message…"
                        value={feedbackSearch}
                        onChange={(e) => setFeedbackSearch(e.target.value)}
                        className="w-full"
                      />
                      <Button
                        variant="outline"
                        className="w-full sm:w-auto"
                        onClick={() => exportFeedbackCSV(filteredFeedback)}
                        disabled={!filteredFeedback.length}
                      >
                        Export CSV
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full sm:w-auto"
                        onClick={() => selectedMenuId && fetchFeedback(selectedMenuId)}
                        disabled={!selectedMenuId || feedbackLoading}
                      >
                        {feedbackLoading ? "Refreshing..." : "Refresh"}
                      </Button>
                    </div>

                    {feedbackLoading ? (
                      <div className="flex items-center justify-center py-10">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <>
                        {/* Mobile cards */}
                        <div className="sm:hidden space-y-3">
                          {filteredFeedback.length === 0 ? (
                            <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
                              No feedback yet.
                            </div>
                          ) : (
                            filteredFeedback.map((r: any) => (
                              <div key={r.id} className="rounded-xl border border-border bg-card p-4 space-y-2">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <div className="font-semibold truncate">{r?.link?.company_name || "—"}</div>
                                    <div className="text-xs text-muted-foreground truncate">
                                      {(r?.employee_name || "—") + " • " + (r?.created_at ? new Date(r.created_at).toLocaleString() : "—")}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-sm whitespace-pre-wrap break-words">{r?.message || ""}</div>
                              </div>
                            ))
                          )}
                        </div>

                        {/* Desktop table */}
                        <div className="hidden sm:block rounded-xl border border-border overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Company</TableHead>
                                <TableHead>Employee</TableHead>
                                <TableHead>Message</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {filteredFeedback.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={4} className="text-sm text-muted-foreground">
                                    No feedback yet.
                                  </TableCell>
                                </TableRow>
                              ) : (
                                filteredFeedback.map((r: any) => (
                                  <TableRow key={r.id}>
                                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                                      {r?.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                                    </TableCell>
                                    <TableCell className="font-medium">{r?.link?.company_name || "—"}</TableCell>
                                    <TableCell className="text-sm">{r?.employee_name || "—"}</TableCell>
                                    <TableCell className="max-w-[680px] whitespace-pre-wrap break-words">
                                      {r?.message || ""}
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </>
                    )}
                  </div>
                ) : null}
              </>
            )}

            {activeTab === "buffet" ? (
                  <div className="space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="w-full sm:max-w-[720px]">
                        <div className="text-xs text-muted-foreground mb-2">Public request form</div>
                        <div className="flex items-center gap-2 bg-background rounded-lg border border-border p-3">
                          <input
                            type="text"
                            value={cateringRequestUrl || "—"}
                            readOnly
                            className="flex-1 bg-transparent text-sm text-foreground outline-none"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={!cateringRequestUrl}
                            onClick={async () => {
                              const ok = await copyToClipboard(cateringRequestUrl || "");
                              if (ok) {
                                toast({ title: "Link copied", description: "Request form link copied to clipboard" });
                              } else {
                                toast({ title: "Copy failed", description: "Please copy the link manually.", variant: "destructive" });
                              }
                            }}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" asChild disabled={!cateringRequestUrl}>
                            <a href={cateringRequestUrl || "#"} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </Button>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={fetchRequests} disabled={requestsLoading}>
                        {requestsLoading ? "Refreshing..." : "Refresh"}
                      </Button>
                    </div>

                    {/* Mobile cards */}
                    <div className="sm:hidden space-y-3">
                      {requests.length === 0 ? (
                        <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
                          No requests yet.
                        </div>
                      ) : (
                        requests.map((r: any) => (
                          <div key={r.id} className="rounded-xl border border-border bg-card p-4 space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="font-semibold truncate">{r.company_name || r.contact_name || "Request"}</div>
                                <div className="text-xs text-muted-foreground truncate">
                                  {r.event_date ? `Event • ${r.event_date}` : "Event • —"} • {r.service_style || "buffet"} •{" "}
                                  {Number(r.attendees || 0)} pax
                                </div>
                              </div>
                              <div className="text-xs font-medium capitalize">{String(r.status || "new").replace(/_/g, " ")}</div>
                            </div>
                            {r.notes ? <div className="text-sm text-muted-foreground line-clamp-3">{r.notes}</div> : null}
                            <div className="grid grid-cols-2 gap-2 pt-2">
                              <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => openEditRequest(r)}
                              >
                                Edit
                              </Button>
                              <Button
                                className="w-full"
                                onClick={() => createInvoiceFromRequest(r)}
                                disabled={!!r.invoice_id}
                              >
                                {r.invoice_id ? "Invoice created" : "Create invoice"}
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Desktop table */}
                    <div className="hidden sm:block rounded-xl border border-border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Company / Contact</TableHead>
                            <TableHead>Event</TableHead>
                            <TableHead>Style</TableHead>
                            <TableHead className="text-right">Pax</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {requests.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={6} className="text-sm text-muted-foreground">
                                No requests yet.
                              </TableCell>
                            </TableRow>
                          ) : (
                            requests.map((r: any) => (
                              <TableRow key={r.id}>
                                <TableCell className="min-w-[220px]">
                                  <div className="font-medium">{r.company_name || r.contact_name || "Request"}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {[r.contact_email, r.contact_phone].filter(Boolean).join(" • ") || "—"}
                                  </div>
                                </TableCell>
                                <TableCell className="text-sm">{r.event_date || "—"}</TableCell>
                                <TableCell className="text-sm capitalize">{String(r.service_style || "buffet")}</TableCell>
                                <TableCell className="text-right">{Number(r.attendees || 0)}</TableCell>
                                <TableCell className="text-sm capitalize">{String(r.status || "new").replace(/_/g, " ")}</TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => openEditRequest(r)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => createInvoiceFromRequest(r)}
                                      disabled={!!r.invoice_id}
                                    >
                                      {r.invoice_id ? "Invoiced" : "Create invoice"}
                                    </Button>
                                    <Select value={String(r.status || "new")} onValueChange={(v) => updateRequestStatus(r.id, v)}>
                                      <SelectTrigger className="w-[150px]">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {["new","contacted","quoted","invoiced","confirmed","delivered","closed","cancelled"].map((s) => (
                                          <SelectItem key={s} value={s}>{s.replace(/_/g," ")}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <CommandDialog open={companyPickerOpen} onOpenChange={setCompanyPickerOpen}>
        <CommandInput
          placeholder="Search companies…"
          value={companySearch}
          onValueChange={(v) => setCompanySearch(v)}
        />
        <CommandList>
          <CommandEmpty>No companies found.</CommandEmpty>
          <CommandGroup heading="Companies">
            {companyResults.map((c: any) => (
              <CommandItem
                key={c.id}
                value={c.name}
                onSelect={() => {
                  setSelectedCompany(c);
                  setCompanyPickerOpen(false);
                }}
              >
                <div className="flex flex-col">
                  <span className="font-medium">{c.name}</span>
                  <span className="text-xs text-muted-foreground">{[c.email, c.phone].filter(Boolean).join(" • ")}</span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>

      <Dialog open={newMenuOpen} onOpenChange={setNewMenuOpen}>
        <DialogContent className="sm:max-w-[520px] w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>Create weekly menu</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0">
            <div className="grid gap-3">
              <div className="space-y-1">
                <Label>Week start date (Monday)</Label>
                <div className="rounded-xl border border-border overflow-hidden">
                  <Calendar
                    mode="single"
                    selected={menuWeekStart ? parseISO(menuWeekStart) : undefined}
                    onSelect={(date) => {
                      if (!date) return;
                      const monday = startOfWeek(date, { weekStartsOn: 1 });
                      setMenuWeekStart(format(monday, "yyyy-MM-dd"));
                    }}
                    className="p-2"
                  />
                </div>
                <div className="text-xs text-muted-foreground">Selected: {menuWeekStart || "—"}</div>
              </div>
              <div className="space-y-1">
                <Label>Title (optional)</Label>
                <Input value={menuTitle} onChange={(e) => setMenuTitle(e.target.value)} placeholder="e.g., Week 1 menu" />
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1">
                  <Label>Cutoff</Label>
                  <Input type="time" value={cutoffTime} onChange={(e) => setCutoffTime(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>Delivery start</Label>
                  <Input type="time" value={deliveryStart} onChange={(e) => setDeliveryStart(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>Delivery end</Label>
                  <Input type="time" value={deliveryEnd} onChange={(e) => setDeliveryEnd(e.target.value)} />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-border bg-background flex-row gap-3 flex-shrink-0">
            <Button variant="outline" onClick={() => setNewMenuOpen(false)} className="flex-1 rounded-lg">
              Cancel
            </Button>
            <Button onClick={createMenu} disabled={creatingMenu || !menuWeekStart} className="flex-1 rounded-lg">
              {creatingMenu ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addMealOpen} onOpenChange={setAddMealOpen}>
        <DialogContent className="sm:max-w-[560px] w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>Add meals</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0">
            <div className="grid gap-3">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1">
                  <Label>Day</Label>
                <Select value={String(mealDay)} onValueChange={(v) => setMealDay(Number(v))}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DOW.map((d) => (
                      <SelectItem key={d.n} value={String(d.n)}>
                        {d.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                </div>
                <div className="sm:col-span-2 flex items-end justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setMealRows((prev) => [...prev, { name: "", price: "0", description: "" }])}
                  >
                    Add another meal
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                {mealRows.map((row, idx) => (
                  <div key={idx} className="rounded-xl border border-border p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-medium">Meal {idx + 1}</div>
                      {mealRows.length > 1 ? (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => setMealRows((prev) => prev.filter((_, i) => i !== idx))}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className="space-y-1 sm:col-span-2">
                        <Label>Meal name</Label>
                        <Input
                          value={row.name}
                          onChange={(e) =>
                            setMealRows((prev) => prev.map((r, i) => (i === idx ? { ...r, name: e.target.value } : r)))
                          }
                          placeholder="e.g., Jollof + chicken"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>Price</Label>
                        <Input
                          value={row.price}
                          onChange={(e) =>
                            setMealRows((prev) => prev.map((r, i) => (i === idx ? { ...r, price: e.target.value } : r)))
                          }
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label>Description (optional)</Label>
                      <Textarea
                        value={row.description}
                        onChange={(e) =>
                          setMealRows((prev) =>
                            prev.map((r, i) => (i === idx ? { ...r, description: e.target.value } : r)),
                          )
                        }
                        placeholder="e.g., with salad"
                        className="min-h-[70px]"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-border bg-background flex-row gap-3 flex-shrink-0">
            <Button variant="outline" onClick={() => setAddMealOpen(false)} className="flex-1 rounded-lg">
              Cancel
            </Button>
            <Button
              onClick={addMealsBatch}
              disabled={addingMeal || mealRows.every((r) => !String(r.name || "").trim())}
              className="flex-1 rounded-lg"
            >
              {addingMeal ? "Adding..." : "Add meals"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Buffet / Events: edit request */}
      <Dialog
        open={editRequestOpen}
        onOpenChange={(v) => {
          setEditRequestOpen(v);
          if (!v) setEditingRequest(null);
        }}
      >
        <DialogContent className="sm:max-w-[720px] w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>Edit request</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0">
            <div className="grid gap-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Company name</Label>
                  <Input
                    value={editReq.company_name || ""}
                    onChange={(e) => setEditReq((x: any) => ({ ...x, company_name: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Status</Label>
                  <Select value={String(editReq.status || "new")} onValueChange={(v) => setEditReq((x: any) => ({ ...x, status: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["new","contacted","quoted","invoiced","confirmed","delivered","closed","cancelled"].map((s) => (
                        <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Event date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-between">
                        <span className={editReq.event_date ? "text-foreground" : "text-muted-foreground"}>
                          {editReq.event_date ? format(parseISO(editReq.event_date), "MMM d, yyyy") : "Select a date"}
                        </span>
                        <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={editReq.event_date ? parseISO(editReq.event_date) : undefined}
                        onSelect={(d) => {
                          if (!d) {
                            setEditReq((x: any) => ({ ...x, event_date: "" }));
                            return;
                          }
                          setEditReq((x: any) => ({ ...x, event_date: format(d, "yyyy-MM-dd") }));
                        }}
                        initialFocus
                        className="p-2"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1">
                  <Label>Attendees</Label>
                  <Input
                    type="number"
                    min={0}
                    value={String(editReq.attendees ?? "0")}
                    onChange={(e) => setEditReq((x: any) => ({ ...x, attendees: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Budget</Label>
                  <Input
                    type="number"
                    min={0}
                    value={editReq.budget_amount || ""}
                    onChange={(e) => setEditReq((x: any) => ({ ...x, budget_amount: e.target.value }))}
                    placeholder="e.g., 1500"
                  />
                  <div className="text-xs text-muted-foreground">
                    Tip: if you create an invoice, we’ll prefill the per‑guest price from this budget.
                  </div>
                </div>
                <div className="space-y-1">
                  <Label>Service style</Label>
                  <Select
                    value={String(editReq.service_style || "buffet")}
                    onValueChange={(v) => setEditReq((x: any) => ({ ...x, service_style: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="buffet">Buffet</SelectItem>
                      <SelectItem value="packed">Packed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Contact name</Label>
                  <Input
                    value={editReq.contact_name || ""}
                    onChange={(e) => setEditReq((x: any) => ({ ...x, contact_name: e.target.value }))}
                    placeholder="Required"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Phone</Label>
                  <Input
                    value={editReq.contact_phone || ""}
                    onChange={(e) => setEditReq((x: any) => ({ ...x, contact_phone: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editReq.contact_email || ""}
                    onChange={(e) => setEditReq((x: any) => ({ ...x, contact_email: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Location</Label>
                  <Input
                    value={editReq.location || ""}
                    onChange={(e) => setEditReq((x: any) => ({ ...x, location: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label>Notes</Label>
                <Textarea
                  value={editReq.notes || ""}
                  onChange={(e) => setEditReq((x: any) => ({ ...x, notes: e.target.value }))}
                  placeholder="Dietary notes, delivery constraints, preferences, etc."
                  className="min-h-28 resize-none"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-border bg-background flex-row gap-3 flex-shrink-0">
            {editingRequest?.invoice_id ? (
              <Button
                variant="outline"
                className="flex-1 rounded-lg"
                onClick={() =>
                  window.open(`${getBaseUrl()}/app/invoicing?edit=${editingRequest.invoice_id}`, "_blank")
                }
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open invoice
              </Button>
            ) : (
              <Button variant="outline" className="flex-1 rounded-lg" onClick={() => setEditRequestOpen(false)}>
                Cancel
              </Button>
            )}
            <Button className="flex-1 rounded-lg" onClick={saveEditedRequest}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}


